import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Typography } from "@mui/material";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AppButton from "../components/buttons/AppButton";
import StandardTable, { TableColumn } from "../components/common/StandardTable";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CreateStudentModal from "../components/students/CreateStudentModal";
import UpdateStudentModal from "../components/students/UpdateStudentModal";
import ViewStudentModal from "../components/students/ViewStudentModal";
import RowActionMenu, {
  RowActionDef,
} from "../components/common/RowActionMenu";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getStudents,
  getStudentById,
  deleteStudent,
  resetStudentServerStatus,
} from "../redux/actions/studentActions";
import { StudentSummaryDto } from "../shared/dtos/student/StudentSummaryDto";
import { StudentResponseDto } from "../shared/dtos/student/StudentResponseDto";

const StudentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  const {
    items: students,
    item: student,
    loadingFetchAll: loadingStudents,
    loadingFetchOne: loadingStudent,
    errorFetchAll: errorLoadingStudents,
    errorFetchOne: errorLoadingStudent,
    pagination,
    serverStatus,
    loadingCreate: loadingStudentCreate,
    errorCreate: errorCreatingStudent,
    loadingUpdate: loadingStudentUpdate,
    errorUpdate: errorUpdatingStudent,
    loadingDelete: loadingStudentDelete,
  } = useAppSelector((state) => state.student);

  // Search and Pagination State
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");
  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });

  // Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [studentForOperation, setStudentForOperation] =
    useState<StudentSummaryDto | null>(null);

  const canCreateStudent = hasPermission("student:create");
  const canEditStudent = hasPermission("student:update");
  const canDeleteStudent = hasPermission("student:delete");
  const canViewStudent = hasPermission("student:read");

  const fetchStudents = useCallback((): void => {
    dispatch(
      getStudents({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch.trim() || undefined,
        branchId: selectedBranchId || undefined,
      }),
    );
  }, [
    dispatch,
    pageConfig.page,
    pageConfig.limit,
    debouncedSearch,
    selectedBranchId,
  ]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingStudentCreate) setIsCreateModalOpen(false);
  }, [loadingStudentCreate]);

  const handleOpenUpdate = (studentRow: StudentSummaryDto): void => {
    dispatch(getStudentById(studentRow.id));
    setStudentForOperation(studentRow);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingStudentUpdate) {
      setIsUpdateModalOpen(false);
      setStudentForOperation(null);
    }
  }, [loadingStudentUpdate]);

  const handleOpenDelete = (studentRow: StudentSummaryDto): void => {
    setStudentForOperation(studentRow);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingStudentDelete) {
      setIsDeleteModalOpen(false);
      setStudentForOperation(null);
    }
  }, [loadingStudentDelete]);

  const handleOpenView = (studentRow: StudentSummaryDto): void => {
    dispatch(getStudentById(studentRow.id));
    setIsViewModalOpen(true);
  };
  const handleCloseView = (): void => setIsViewModalOpen(false);

  const handleDeleteConfirm = (): void => {
    if (studentForOperation) {
      dispatch(deleteStudent(studentForOperation.id));
    }
  };

  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && (status === 201 || status === 200))
      handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetStudentServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  const columns = useMemo<TableColumn<StudentSummaryDto>[]>(
    () => [
      { id: "studentId", label: "Student ID", minWidth: 120 },
      {
        id: "name",
        label: "Full Name",
        minWidth: 170,
        format: (_, row) => (
          <Typography
            component="span"
            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
          >
            {row.firstName} {row.middleName ? `${row.middleName} ` : ""}
            {row.lastName}
          </Typography>
        ),
      },
      { id: "gender", label: "Gender", minWidth: 100 },
      { id: "branchName", label: "Branch", minWidth: 150 },
      { id: "gradeLevelName", label: "Grade Level", minWidth: 130 },
      { id: "classroomName", label: "Classroom", minWidth: 130 },
    ],
    [],
  );

  const renderRowActions = (row: StudentSummaryDto): React.ReactNode => {
    const inlineActions: RowActionDef[] = [
      {
        label: "View",
        icon: <VisibilityOutlinedIcon fontSize="small" />,
        color: "info",
        onClick: () => handleOpenView(row),
        show: canViewStudent,
      },
    ];

    const menuActions: RowActionDef[] = [
      {
        label: "Edit Student",
        icon: <EditOutlinedIcon fontSize="small" />,
        onClick: () => handleOpenUpdate(row),
        show: canEditStudent,
      },
      {
        label: "Delete Student",
        icon: <DeleteOutlineIcon fontSize="small" />,
        color: "error",
        onClick: () => handleOpenDelete(row),
        show: canDeleteStudent,
      },
    ];

    return (
      <RowActionMenu inlineActions={inlineActions} menuActions={menuActions} />
    );
  };

  return (
    <PageContainer
      title="Student Management"
      subtitle="Manage student enrollments, profiles, and campus placements."
      actions={
        canCreateStudent && (
          <AppButton
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenCreate}
          >
            Register Student
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPageConfig((p) => ({ ...p, page: 1 }));
        }}
        searchPlaceholder="Search students by name or ID..."
      >
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => {
            setSelectedBranchId(bId);
            setPageConfig((p) => ({ ...p, page: 1 }));
          }}
        />
      </PageToolbar>

      <StandardTable<StudentSummaryDto>
        columns={columns}
        data={students}
        loading={loadingStudents}
        error={errorLoadingStudents}
        onRetry={fetchStudents}
        page={pageConfig.page}
        count={pagination?.totalElements || 0}
        rowsPerPage={pageConfig.limit}
        onPageChange={(newPage) =>
          setPageConfig((prev) => ({ ...prev, page: newPage }))
        }
        onRowsPerPageChange={(newLimit) =>
          setPageConfig((prev) => ({ ...prev, limit: newLimit, page: 1 }))
        }
        rowAction={renderRowActions}
      />

      {isCreateModalOpen && (
        <CreateStudentModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingStudentCreate}
          error={errorCreatingStudent}
        />
      )}

      {isUpdateModalOpen && studentForOperation && (
        <UpdateStudentModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          student={
            (student?.id === studentForOperation.id
              ? student
              : studentForOperation) as StudentResponseDto
          }
          loading={loadingStudentUpdate || loadingStudent}
          error={errorUpdatingStudent || errorLoadingStudent}
        />
      )}

      {isViewModalOpen && (
        <ViewStudentModal
          open={isViewModalOpen}
          onClose={handleCloseView}
          student={student}
          loading={loadingStudent}
          error={errorLoadingStudent}
        />
      )}

      {isDeleteModalOpen && studentForOperation && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Student"
          description={`Are you sure you want to delete the student "${studentForOperation.firstName} ${studentForOperation.lastName}"? This action cannot be undone.`}
        />
      )}
    </PageContainer>
  );
};

export default StudentsPage;
